import { requestOtpAuth as requestOtp } from '@api/generated/sdk.gen';
import { parseApiError } from '@shared/lib/api-error';
import { isPhoneValid, toE164 } from '@shared/lib/phone';
import { AuthShell } from '@shared/ui/AuthShell';
import { PhoneInput } from '@shared/ui/PhoneInput';
import { Alert, Button, Form, Typography } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

const { Text } = Typography;

type FormValues = {
  phone: string;
};

/**
 * Страница входа: запрос SMS-кода по номеру телефона.
 *
 * Номер форматируется as-you-type через libphonenumber-js (см. `PhoneInput`),
 * валидируется по правилам страны, перед отправкой конвертируется в E.164.
 * После успешного `/otp/request` навигирует на `/verify-otp`.
 */
export function LoginPage() {
  const { t } = useTranslation(['auth', 'errors']);
  const navigate = useNavigate();
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (values: FormValues): Promise<void> => {
    setErrorCode(null);
    const e164 = toE164(values.phone);
    if (!e164) {
      setErrorCode('INVALID_PHONE');
      return;
    }
    setLoading(true);
    try {
      await requestOtp({ body: { phone: e164 } });
      void navigate('/verify-otp', { state: { phone: e164 } });
    } catch (err: unknown) {
      const apiErr = parseApiError(
        (err as { response?: { data?: unknown } }).response?.data,
        (err as { response?: { status?: number } }).response?.status,
      );
      setErrorCode(apiErr.code);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={t('auth:login.title')}
      subtitle={t('auth:login.subtitle')}
      footer={
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t('auth:login.footer')}
        </Text>
      }
    >
      {errorCode && (
        <Alert
          type="error"
          showIcon
          message={t(`errors:${errorCode}`, { defaultValue: t('errors:UNKNOWN_ERROR') })}
          style={{ marginBottom: 20 }}
          closable
          onClose={() => {
            setErrorCode(null);
          }}
        />
      )}

      <Form<FormValues> layout="vertical" onFinish={(v) => void onSubmit(v)} requiredMark={false}>
        <Form.Item
          label={t('auth:login.phoneLabel')}
          name="phone"
          rules={[
            { required: true, message: t('errors:fields.NOT_BLANK') },
            {
              validator: (_, value: string) => {
                if (value && !isPhoneValid(value)) {
                  return Promise.reject(new Error(t('errors:INVALID_PHONE')));
                }
                return Promise.resolve();
              },
            },
          ]}
          extra={t('auth:login.phoneHelp')}
        >
          <PhoneInput placeholder={t('auth:login.phonePlaceholder')} autoFocus size="large" />
        </Form.Item>

        <Button type="primary" htmlType="submit" loading={loading} block size="large">
          {t('auth:login.submit')}
        </Button>
      </Form>
    </AuthShell>
  );
}
